import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  TablePagination,
  LinearProgress,
  Checkbox,
  Tooltip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Search,
  Refresh,
  Download,
  CheckCircle,
  OpenInNew,
  Description,
  Tune,
  PictureAsPdf,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import sailingService from "../../services/sailingService";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import CustomsOverridesDialog from "../../components/customs/CustomsOverridesDialog";
import { formatUsd } from "../../utils/currency";
import { slugifyTab } from "../../utils/tabSlug";

// "" (default) shows the real customs queue — shipments with at least one
// item in "Awaiting Customs Clearance" — as computed server-side. The other
// options filter by the shipment's own aggregate status instead, for
// finding shipments that aren't in the queue yet (still missing an invoice
// document) or that already have every item further along.
const STATUS_FILTERS = [
  { value: "", label: "Awaiting Customs Clearance (queue)" },
  { value: "awaiting_invoice", label: "Awaiting Invoice" },
  { value: "in_transit", label: "In Transit" },
];

const CUSTOMS_QUEUE_URL_SCHEMA = {
  search: { default: "" },
  statusFilter: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

// ── Customs Queue tab (existing page content, unchanged) ─────────────────────
const CustomsQueueTab = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(CUSTOMS_QUEUE_URL_SCHEMA);
  const { statusFilter, page, rowsPerPage } = urlState;
  // Local typing buffer — see AdminShipments.jsx's identical comment for why
  // this isn't synced to the URL on every keystroke, only on Enter/commit.
  const [search, setSearch] = useState(urlState.search);
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);

  // Dialogs
  const [xmlDialogOpen, setXmlDialogOpen] = useState(false);
  const [clearTarget, setClearTarget] = useState(null); // single shipment to mark cleared
  const [clearingId, setClearingId] = useState(null);
  const [clearNotes, setClearNotes] = useState("");

  const load = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const { data } = await api.get("/customs", {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            status: statusFilter || undefined,
            search: search || undefined,
            ...overrides,
          },
        });
        setShipments(data.data || []);
        setTotal(data.pagination?.total || 0);
      } catch {
        enqueueSnackbar("Failed to load customs queue", { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, statusFilter, search, enqueueSnackbar],
  );

  useEffect(() => {
    load();
  }, [page, rowsPerPage, statusFilter]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setUrlState({ search: e.target.value, page: 0 });
      load({ page: 1, search: e.target.value });
    }
  };

  // ── Selection helpers ────────────────────────────────────────────────────────
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === shipments.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(shipments.map((s) => s._id)));
    }
  };

  const selectedShipments = shipments.filter((s) => selected.has(s._id));

  // ── Mark cleared ─────────────────────────────────────────────────────────────
  const handleMarkCleared = async () => {
    if (!clearTarget) return;
    setClearingId(clearTarget._id);
    try {
      await api.patch(`/customs/${clearTarget._id}/clear`, {
        notes: clearNotes,
      });
      enqueueSnackbar(
        `${clearTarget.trackingNumber} marked as customs cleared`,
        { variant: "success" },
      );
      setClearTarget(null);
      setClearNotes("");
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(clearTarget._id);
        return next;
      });
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to mark cleared", {
        variant: "error",
      });
    } finally {
      setClearingId(null);
    }
  };

  const allSelected =
    shipments.length > 0 && selected.size === shipments.length;
  const someSelected = selected.size > 0 && selected.size < shipments.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {total} shipments awaiting customs action
        </Typography>
        {selected.size > 0 && (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckCircle />}
              onClick={() => {
                if (selected.size === 1) {
                  const s = shipments.find((s) => selected.has(s._id));
                  setClearTarget(s);
                }
              }}
              disabled={selected.size !== 1}
            >
              Mark Cleared
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Download />}
              onClick={() => setXmlDialogOpen(true)}
            >
              Generate XML ({selected.size})
            </Button>
          </Box>
        )}
      </Box>

      {/* Selection banner */}
      {selected.size > 0 && (
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <Button
              size="small"
              color="info"
              onClick={() => setSelected(new Set())}
            >
              Clear Selection
            </Button>
          }
        >
          <strong>{selected.size}</strong> shipment
          {selected.size !== 1 ? "s" : ""} selected ·{" "}
          {selected.size === 1
            ? "Generate XML or mark as cleared."
            : "Generate one combined XML declaration for all selected shipments."}
        </Alert>
      )}

      <Card>
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search tracking #, vendor, description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            sx={{ width: 280 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setUrlState({ statusFilter: e.target.value, page: 0 })}
            sx={{ width: 200 }}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => load()}>
              <Refresh />
            </IconButton>
          </Tooltip>

          {/* Bulk generate all selected */}
          {selected.size > 1 && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Download />}
              onClick={() => setXmlDialogOpen(true)}
              sx={{ ml: "auto" }}
            >
              Generate Combined XML ({selected.size})
            </Button>
          )}
        </Box>

        {loading && <LinearProgress />}

        {!loading && shipments.length === 0 ? (
          <EmptyState
            icon="📋"
            title="No shipments in customs queue"
            description="Shipments with an item anywhere from Sailing through Arrived Cayman / Awaiting Customs Clearance (i.e. not yet cleared) will appear here."
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                      />
                    </TableCell>
                    <TableCell>Tracking #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell align="right">Declared Value</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sailing</TableCell>
                    <TableCell align="center">Items</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => {
                    const isSelected = selected.has(s._id);
                    const isExpanded = expandedId === s._id;
                    const items = s.items || [];
                    const sailingNumber =
                      items.find((it) => it.sailingSchedule)?.sailingSchedule?.sailingNumber;
                    return (
                      <React.Fragment key={s._id}>
                        <TableRow
                          hover
                          selected={isSelected}
                          sx={{
                            cursor: "pointer",
                            bgcolor: isSelected
                              ? "rgba(13,98,155,0.04)"
                              : "transparent",
                          }}
                          onClick={() => setExpandedId(isExpanded ? null : s._id)}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleOne(s._id)}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.trackingNumber}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="body2" fontWeight={600}>
                              {s.customer?.firstName} {s.customer?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {s.customer?.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap maxWidth={200}>
                              {s.packageDescription || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ color: "text.secondary", fontSize: 13 }}
                          >
                            {s.vendor?.name}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatUsd(s.declaredValue)}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={s.status} domain="shipment" />
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            {sailingNumber || "—"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={items.length} variant="outlined" />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 12,
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dayjs(s.createdAt).format("MMM D, YYYY")}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Generate XML">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelected(new Set([s._id]));
                                    setXmlDialogOpen(true);
                                  }}
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Mark Customs Cleared">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => setClearTarget(s)}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open Shipment">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    navigate(`/admin/shipments/${s.trackingNumber}`)
                                  }
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedId(isExpanded ? null : s._id)}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUp fontSize="small" />
                              ) : (
                                <KeyboardArrowDown fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={12} sx={{ py: 2, bgcolor: "action.hover" }}>
                              {items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                                  No items currently between Sailing and Customs Cleared on this shipment.
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Description</TableCell>
                                      <TableCell>Tracking #</TableCell>
                                      <TableCell>Dock Receipt #</TableCell>
                                      <TableCell>Container #</TableCell>
                                      <TableCell>Item Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {items.map((it) => (
                                      <TableRow key={it._id}>
                                        <TableCell>{it.description}</TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                          {it.trackingNumber || "—"}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                          {it.dockReceiptNumber || "—"}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                          {it.containerNumber || "—"}
                                        </TableCell>
                                        <TableCell>
                                          <StatusChip status={it.itemStatus} domain="item" />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setUrlState({ page: p })}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>

      {/* XML Generation Dialog */}
      <CustomsOverridesDialog
        open={xmlDialogOpen}
        shipments={selectedShipments}
        onClose={() => setXmlDialogOpen(false)}
      />

      {/* Mark Cleared Confirm */}
      <ConfirmDialog
        open={!!clearTarget}
        title="Mark as Customs Cleared?"
        message={
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              This will move <strong>{clearTarget?.trackingNumber}</strong> to{" "}
              <strong>Customs Cleared</strong> status and notify the customer.
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Notes (optional)"
              placeholder="e.g. Duties paid — $420.00"
              value={clearNotes}
              onChange={(e) => setClearNotes(e.target.value)}
            />
          </Box>
        }
        confirmLabel="Mark Cleared"
        confirmColor="success"
        loading={!!clearingId}
        onConfirm={handleMarkCleared}
        onCancel={() => {
          setClearTarget(null);
          setClearNotes("");
        }}
      />
    </Box>
  );
};

// ── Manifest tab — pick a sailing, view the full consolidated manifest ───────
const ManifestTab = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [sailings, setSailings] = useState([]);
  const [sailingId, setSailingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingInvoices, setDownloadingInvoices] = useState(false);

  useEffect(() => {
    sailingService
      .getAll()
      .then(({ data }) => {
        // Recent-first by estimated arrival date, not the endpoint's own
        // departure-date-ascending default — scoped to just this
        // Manifest/HBOL picker, not the shared /sailing endpoint other
        // pages rely on in chronological order.
        const schedules = [...(data.data?.schedules || [])].sort(
          (a, b) => new Date(b.estimatedArrivalDate || 0) - new Date(a.estimatedArrivalDate || 0),
        );
        setSailings(schedules);
      })
      .catch(() => enqueueSnackbar("Failed to load sailing schedules", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadBlobResponse = (response, fallbackFilename) => {
    const disposition = response.headers?.["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : fallbackFilename;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadXlsx = async () => {
    setDownloading(true);
    try {
      const response = await sailingService.getManifestXlsx(sailingId);
      downloadBlobResponse(response, "Manifest.xlsx");
    } catch {
      enqueueSnackbar("Failed to download manifest Excel file", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await sailingService.getManifestPdf(sailingId);
      downloadBlobResponse(response, "Manifest.pdf");
    } catch {
      enqueueSnackbar("Failed to download manifest PDF", { variant: "error" });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadInvoices = async () => {
    setDownloadingInvoices(true);
    try {
      const response = await sailingService.getInvoicesZip(sailingId);
      downloadBlobResponse(response, "Purchase_Invoices.zip");
    } catch {
      enqueueSnackbar("Failed to download purchase invoices — none may be on file for this sailing", {
        variant: "error",
      });
    } finally {
      setDownloadingInvoices(false);
    }
  };

  return (
    <Card sx={{ p: 3, maxWidth: "100%" }}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Select a sailing to view its consolidated cargo manifest — one row per physical box,
        across every customer assigned to that sailing.
      </Typography>
      <TextField
        select
        fullWidth
        label="Sailing"
        value={sailingId}
        onChange={(e) => setSailingId(e.target.value)}
        disabled={loading}
        sx={{ mb: 2, mt: 2 }}
      >
        {/* Completed sailings are done and archived — nothing left to
            manifest/allocate for them, so they're excluded here rather than
            just left disabled, keeping the list to sailings still in play. */}
        {sailings
          .filter((s) => s.status !== "completed")
          .map((s) => (
            <MenuItem key={s._id} value={s.sailingNumber}>
              {s.sailingNumber} — {s.vesselName} {s.voyageNumber ? `/ ${s.voyageNumber}` : ""} ({dayjs(s.departureDate).format("MMM D, YYYY")})
            </MenuItem>
          ))}
      </TextField>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<Description />}
          disabled={!sailingId}
          onClick={() => navigate(`/admin/sailing/${sailingId}/manifest`)}
        >
          View Manifest
        </Button>
        <Button
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
          disabled={!sailingId || downloading}
          onClick={handleDownloadXlsx}
        >
          Download Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
          disabled={!sailingId || downloadingPdf}
          onClick={handleDownloadPdf}
        >
          Download PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={<Tune />}
          disabled={!sailingId}
          onClick={() => navigate(`/admin/sailing/${sailingId}/freight-allocation`)}
        >
          Edit Freight Allocation
        </Button>
        <Button
          variant="outlined"
          startIcon={downloadingInvoices ? <CircularProgress size={16} /> : <Download />}
          disabled={!sailingId || downloadingInvoices}
          onClick={handleDownloadInvoices}
        >
          Download Purchase Invoices
        </Button>
      </Box>
    </Card>
  );
};

// ── House BOL tab — pick a sailing, then a customer on that sailing ──────────
const HouseBolTab = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [sailings, setSailings] = useState([]);
  const [sailingId, setSailingId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  // Which TINs this customer actually has shipments under on THIS sailing
  // — keyed by customerId, each value a Set of tin strings (may contain
  // just "" for a single-TIN customer/legacy shipment).
  const [tinsByCustomer, setTinsByCustomer] = useState(new Map());
  const [tin, setTin] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSailing, setLoadingSailing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const selectedCustomer = customers.find((c) => (c._id || c) === customerId);
  // Only worth showing a TIN picker when the customer actually manages
  // multiple TINs AND has shipments under more than one of them on this
  // particular sailing — a multi-TIN customer with everything filed under
  // just one TIN here doesn't need to be asked to pick.
  const tinOptionsForCustomer = selectedCustomer?.canManageMultipleTins
    ? (selectedCustomer.tins || []).filter((t) => tinsByCustomer.get(customerId)?.has(t.tin))
    : [];
  const needsTinPicker = tinOptionsForCustomer.length > 1;

  useEffect(() => {
    sailingService
      .getAll()
      .then(({ data }) => {
        // Recent-first by estimated arrival date, not the endpoint's own
        // departure-date-ascending default — scoped to just this
        // Manifest/HBOL picker, not the shared /sailing endpoint other
        // pages rely on in chronological order.
        const schedules = [...(data.data?.schedules || [])].sort(
          (a, b) => new Date(b.estimatedArrivalDate || 0) - new Date(a.estimatedArrivalDate || 0),
        );
        setSailings(schedules);
      })
      .catch(() => enqueueSnackbar("Failed to load sailing schedules", { variant: "error" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadHouseBol = async () => {
    setDownloading(true);
    try {
      const response = await sailingService.getHouseBolPdf(sailingId, customerId, tin);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : "HBOL.pdf";

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar("Failed to download House BOL", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setCustomerId("");
    setCustomers([]);
    setTinsByCustomer(new Map());
    setTin("");
    if (!sailingId) return;
    setLoadingSailing(true);
    sailingService
      .getOne(sailingId)
      .then(({ data }) => {
        // sailing.items is a roster of { shipment (populated), itemId } —
        // not a `shipments` array — mirrors AdminSailingDetail.jsx.
        const entries = data.data?.schedule?.items || [];
        const byCustomer = new Map();
        const tinsMap = new Map();
        entries.forEach((entry) => {
          const c = entry.shipment?.customer;
          const cId = c?._id || c;
          if (!c) return;
          if (!byCustomer.has(cId)) byCustomer.set(cId, c);
          if (!tinsMap.has(cId)) tinsMap.set(cId, new Set());
          tinsMap.get(cId).add(entry.shipment?.tin || "");
        });
        setCustomers([...byCustomer.values()]);
        setTinsByCustomer(tinsMap);
      })
      .catch(() => enqueueSnackbar("Failed to load shipments for this sailing", { variant: "error" }))
      .finally(() => setLoadingSailing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sailingId]);

  // Reset the TIN pick whenever the customer changes — a stale TIN from a
  // previous selection must never carry over onto a different customer.
  // Auto-select from tinsByCustomer (the ACTUAL tin value(s) found on this
  // customer's shipments on this sailing), not tinOptionsForCustomer (that
  // one's just customer.tins metadata for populating the multi-TIN
  // dropdown, and is unconditionally [] for a single-TIN customer). A
  // single-TIN customer's shipments still carry their real TIN in
  // shipment.tin (set from customer.tin at creation — see
  // shipmentController.createShipment) — it's essentially never "", so
  // relying on tinOptionsForCustomer here left `tin` stuck at "" for
  // every single-TIN customer, and the server's House BOL lookup
  // (`Shipment.find({ tin: tin || "" })`) then matched nothing.
  useEffect(() => {
    const tinsForCustomer = [...(tinsByCustomer.get(customerId) || [])];
    setTin(tinsForCustomer.length === 1 ? tinsForCustomer[0] : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, tinsByCustomer]);

  return (
    <>
      <Card sx={{ p: 3, maxWidth: 520, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a sailing, then a customer on that sailing, to view their House Bill of Lading.
        </Typography>
        <TextField
          select
          fullWidth
          label="Sailing"
          value={sailingId}
          onChange={(e) => setSailingId(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {/* Completed sailings are done and archived — nothing left to
            issue a House BOL for, so they're excluded here rather than just
            left disabled, keeping the list to sailings still in play. */}
          {sailings
            .filter((s) => s.status !== "completed")
            .map((s) => (
              <MenuItem key={s._id} value={s.sailingNumber}>
                {s.sailingNumber} — {s.vesselName} {s.voyageNumber ? `/ ${s.voyageNumber}` : ""} ({dayjs(s.departureDate).format("MMM D, YYYY")})
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Customer"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          disabled={!sailingId || loadingSailing}
          helperText={sailingId && !loadingSailing && customers.length === 0 ? "No shipments with a customer on this sailing" : ""}
          sx={{ mb: 2 }}
        >
          {customers.map((c) => (
            <MenuItem key={c._id || c} value={c._id || c}>
              {c.firstName} {c.lastName}
            </MenuItem>
          ))}
        </TextField>

        {/* Only shown for a customer who both manages multiple TINs AND has
          shipments under more than one of them on THIS sailing — each TIN
          is its own separate House Bill (see houseBolService.js). */}
        {needsTinPicker && (
          <TextField
            select
            fullWidth
            label="TIN"
            value={tin}
            onChange={(e) => setTin(e.target.value)}
            helperText="This customer has shipments under more than one TIN on this sailing — pick which one this House BOL is for."
            sx={{ mb: 2 }}
          >
            {tinOptionsForCustomer.map((t) => (
              <MenuItem key={t.tin} value={t.tin}>
                {t.label ? `${t.tin} (${t.label})` : t.tin}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<Description />}
            disabled={!sailingId || !customerId || (needsTinPicker && !tin)}
            onClick={() =>
              navigate(`/admin/sailing/${sailingId}/house-bol/${customerId}${tin ? `?tin=${encodeURIComponent(tin)}` : ""}`)
            }
          >
            View House BOL
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} /> : <PictureAsPdf />}
            disabled={!sailingId || !customerId || downloading || (needsTinPicker && !tin)}
            onClick={handleDownloadHouseBol}
          >
            Download PDF
          </Button>
        </Box>
      </Card>

    </>
  );
};

// SAD XML generation isn't its own tab — it happens directly from Customs
// Queue (per-row "Generate XML" / bulk "Generate Combined XML", see CustomsQueueTab
// above), so there's nothing separate to show here. A disabled "XML" tab
// used to sit here claiming this was "planned but not yet built," which was
// stale/misleading since the real feature already works from Customs Queue.
const TAB_LABELS = ["Customs Queue", "Manifest", "House BOL"];
const ADMIN_CUSTOMS_URL_SCHEMA = {
  // The tab's slugified label ("customs_queue", "manifest", …) rather than
  // its array index — stable if tabs are ever reordered, and readable in
  // the address bar/shared links instead of a meaningless number.
  tab: { default: "" },
};

const AdminCustoms = () => {
  const [{ tab }, setUrlState] = useUrlState(ADMIN_CUSTOMS_URL_SCHEMA);
  const activeTab = Math.max(0, TAB_LABELS.findIndex((label) => slugifyTab(label) === tab));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader title="Customs" subtitle="Customs queue, House Bill of Lading, and consolidated manifest" />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setUrlState({ tab: slugifyTab(TAB_LABELS[v]) })}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ maxWidth: "100%", borderBottom: "1px solid", borderColor: "divider" }}
      >
        {TAB_LABELS.map((label) => (
          <Tab key={label} label={label} sx={{ textTransform: "none", fontWeight: 600 }} />
        ))}
      </Tabs>

      {activeTab === 0 && <CustomsQueueTab />}
      {activeTab === 1 && <ManifestTab />}
      {activeTab === 2 && <HouseBolTab />}
    </Box>
  );
};

export default AdminCustoms;
